import Routes from '@common/defs/routes';
import { useEffect } from 'react';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { useRouter } from 'next/router';
import { Box, Typography, Link as MuiLink, CircularProgress } from '@mui/material';
import Head from 'next/head';
import Logo from '@common/assets/svgs/Logo';

// Le HTML SSR de cette page sert de surface visible pour Googlebot et autres crawlers :
// description claire de l'app + lien Privacy Policy en dur dans le footer. Sans ça, le
// crawler voyait un HTML quasi vide (redirect 100% côté client via withAuth) et Google
// flaggait la home en "missing privacy policy link" / "branding guidelines" lors de la
// vérification OAuth des scopes Calendar.
// Côté utilisateur, useEffect redirige immédiatement après hydration vers /lp (non
// connecté) ou /dashboard (connecté). Le skeleton n'est visible que pendant ~100ms.

const HomePage = () => {
  const router = useRouter();
  const { user, initialized } = useAuth();

  const getOAuthQueryParams = () => {
    const keysToForward = [
      'connection',
      'provider',
      'message',
      'error_code',
      'error_message',
      'email',
    ] as const;

    return keysToForward.reduce<Record<string, string>>((acc, key) => {
      const value = router.query[key];
      if (typeof value === 'string' && value.trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    if (!router.isReady || !initialized) {
      return;
    }

    const forwardedQuery = getOAuthQueryParams();
    const hasForwardedQuery = Object.keys(forwardedQuery).length > 0;

    if (!user) {
      if (hasForwardedQuery) {
        router.replace({ pathname: Routes.LP, query: forwardedQuery });
      } else {
        router.replace(Routes.LP);
      }
      return;
    }

    if (user?.hasActiveSx || user?.isDemo) {
      if (hasForwardedQuery) {
        router.replace({ pathname: Routes.SystemX, query: forwardedQuery });
      } else {
        router.replace(Routes.SystemX);
      }
    } else if (hasForwardedQuery) {
      router.replace({ pathname: Routes.Leads.MyClients, query: forwardedQuery });
    } else {
      router.replace(Routes.Leads.MyClients);
    }
  }, [router, router.isReady, user, initialized]);

  return (
    <>
      <Head>
        <title>AlloClients — Automatisez votre agenda thérapeute</title>
        <meta
          name="description"
          content="AlloClients aide les thérapeutes (réflexologues, naturopathes, sophrologues) à remplir leur agenda automatiquement, gérer leurs rendez-vous et leur facturation. Synchronisation Google Agenda, paiement en ligne, communication client."
        />
      </Head>
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          p: 4,
          bgcolor: '#fff',
        }}
      >
        <Logo sx={{ height: 60, maxWidth: '100%' }} />
        <Box sx={{ textAlign: 'center', maxWidth: 640 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={600}
            gutterBottom
            sx={{ color: 'rgb(50, 50, 50)' }}
          >
            AlloClients
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            L&apos;application qui aide les thérapeutes à remplir leur agenda automatiquement, gérer
            leurs rendez-vous et leur facturation.
          </Typography>
        </Box>
        <CircularProgress size={28} sx={{ mt: 2 }} aria-label="Chargement de l'application" />
        <Box
          component="footer"
          sx={{
            mt: 6,
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            justifyContent: 'center',
            fontSize: '0.875rem',
          }}
        >
          <MuiLink
            href="/legal/politique-de-confidentialite"
            underline="hover"
            color="text.secondary"
          >
            Politique de confidentialité
          </MuiLink>
          <MuiLink href="https://www.allo-clients.com" underline="hover" color="text.secondary">
            Site officiel
          </MuiLink>
        </Box>
      </Box>
    </>
  );
};

// Force SSR à chaque requête pour que le HTML servi à Googlebot/Trust&Safety contienne
// le Logo, la description et le lien Privacy Policy. Sans ça, Next.js fait un autoExport
// qui produit un body vide (le composant est client-rendered uniquement) et Google flag
// "missing privacy policy link" alors que le lien est bien rendu côté client.
export const getServerSideProps = async () => {
  return { props: {} };
};

export default HomePage;
